//Node Modules
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/client";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { useAtomValue } from "jotai";

//My Components
import { TeamMember } from "./";

//3rd Party Components

//STATE
import { previewContentAtom } from "../../state/appState";

//Config

//Helpers
import { GET_TEAM_MEMBERS } from "../../graphql/team";

//Other

const TeamMembers = () => {
	//Hooks
	const { t } = useTranslation();
	const teamMembersWrapperRef = useRef();

	//Local State
	const [selectedTeamMember, setSelectedTeamMember] = useState();

	//Atom State
	const isPreviewContent = useAtomValue(previewContentAtom);

	//GraphQL Setup
	const [getTeamMembers, { loading, error, data }] = useLazyQuery(GET_TEAM_MEMBERS, {
		variables: {
			isPreview: isPreviewContent,
		},
	});

	useEffect(() => {
		getTeamMembers();
	}, []);

	const handleTeamMemberSelect = (teamMemberId) => {
		// Check if the browser supports the API (to avoid crashes in older browsers)
		if (!document.startViewTransition) {
			if (teamMemberId) setSelectedTeamMember(teamMemberId);
			else setSelectedTeamMember(null);
			return;
		}

		// Trigger the transition
		document.startViewTransition(() => {
			if (teamMemberId) setSelectedTeamMember(teamMemberId);
			else setSelectedTeamMember(null);
		});

		teamMembersWrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
	};

	if (error) {
		return <h1>ERROR</h1>;
	} else if (loading) {
		return <h1>FETCHING TEAM MEMBERS...</h1>;
	} else if (data) {
		const {
			teamCollection: { items: teamMembers },
		} = data;

		const jobTitlePriority = ["Director", "Associate", "Head of Interior Design"];
		const sortedTeamMembers = [...teamMembers].sort((a, b) => {
			//First we sort on the job title
			const jobPriorityA = jobTitlePriority.indexOf(a.jobTitle);
			const jobPriorityB = jobTitlePriority.indexOf(b.jobTitle);

			//Next we sort of priority if available
			const setPriorityA = a.priority || 0;
			const setPriorityB = b.priority || 0;

			//Next we sort on surname
			const surnameA = a.name.split(" ").at(-1);
			const surnameB = b.name.split(" ").at(-1);

			return jobPriorityA - jobPriorityB || setPriorityB - setPriorityA || surnameA.localeCompare(surnameB);
		});

		return (
			<>
				<div ref={teamMembersWrapperRef} className="grid-columns-autoFill teamMemberItem-wrapper marginTop-large">
					{!selectedTeamMember && <div className="span-all"></div>}
					{sortedTeamMembers.map((teamMember) => (
						<div
							key={teamMember.sys.id}
							className="teamMemberItem"
							data-selected={teamMember.sys.id === selectedTeamMember}
							onClick={() => teamMember.sys.id !== selectedTeamMember && handleTeamMemberSelect(teamMember.sys.id)}
							style={{
								viewTransitionName: `teamMemberTransition-${teamMember.sys.id}`,
								order: teamMember.sys.id === selectedTeamMember ? -1 : 0,
								gridColumn: teamMember.sys.id === selectedTeamMember ? "1 / -1" : "auto",
							}}>
							<img src={`${teamMember.portrait.url}?w=800`} alt={`${teamMember.name} Portrait`} className="teamMemberItem-portrait" />
							<div className="teamMemberItem-info">
								<div className="flex gapCol-small jContent-stretch aItems-end">
									<span className="teamMemberItem-name">{teamMember.name}</span>
									{teamMember.sys.id === selectedTeamMember && <span className="teamMemberItem-nominals">{teamMember.postNominals}</span>}
								</div>
								{teamMember.sys.id !== selectedTeamMember && <i className="bi bi-arrow-right-short fontSize-medium teamMemeberItem-details" />}
								<span className="teamMemberItem-jobTitle">{teamMember.jobTitle}</span>
								{teamMember.sys.id === selectedTeamMember && <p className="marginBlock-small">{documentToReactComponents(teamMember.biography.json)}</p>}
								{teamMember.sys.id === selectedTeamMember && (
									<div className="flex flex-wrap gap-min">
										{teamMember.qualifications.map((quali) => (
											<span className="teamMemberItem-quali">{quali}</span>
										))}
									</div>
								)}
							</div>
							{teamMember.sys.id === selectedTeamMember && <i className="bi bi-x teamMemberItem-close" onClick={() => handleTeamMemberSelect(null)} />}
						</div>
					))}
				</div>
			</>
		);
	}
};

export default TeamMembers;
